import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Menu,
  MenuItem,
  Badge,
  Chip,
  // CircularProgress,
  LinearProgress,
  Fade,
  Grow,
  Zoom,
  Slide,
  Collapse,
  useMediaQuery,
  useTheme,
  alpha,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  Restaurant as RestaurantIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  WaterDrop as WaterDropIcon,
  Recycling as RecyclingIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Cloud as CloudIcon,
  ElectricBolt as ElectricBoltIcon,
  // GasMeter as GasMeterIcon,
  Calculate as CalculateIcon,
  EmojiEvents as EmojiEventsIcon,
  Bolt as BoltIcon,
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronRightIcon,
  WbSunny as WbSunnyIcon,
  // Air as AirIcon,
  Opacity as OpacityIcon,
  // MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { useAuth } from '../../contexts/AuthContext';
import Overview from './Overview';
import CarbonFootprint from './CarbonFootprint';
import EnergyManagement from './EnergyManagement';
import WasteManagement from './WasteManagement';
import StaffManagement from './StaffManagement';
import ClockLoader from '../common/ClockLoader';
import POSIntegration from './POSIntegration';
import POSCallback from './POSCallback';
import { styled, keyframes } from '@mui/material/styles';

const drawerWidth = 260;
const drawerWidthMobile = 280;

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// const shimmerAnimation = keyframes`
//   0% { background-position: -1000px 0; }
//   100% { background-position: 1000px 0; }
// `;

const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(10deg); }
`;

// const dotPulse = keyframes`
//   0% { transform: scale(0.6); opacity: 0.6; }
//   50% { transform: scale(1); opacity: 1; }
//   100% { transform: scale(0.6); opacity: 0.6; }
// `;

// Styled Components
const AnimatedCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '16px',
  transition: 'transform 0.35s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.35s',
  animation: `${fadeInUp} 0.6s ease-out`,
  background: alpha(theme.palette.background.paper, 0.9),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.06)}`,
  backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.02)}, transparent 40%)`,
  '&:hover': {
    transform: 'translateY(-10px) scale(1.02)',
    boxShadow: `0 28px 80px ${alpha(theme.palette.primary.main, 0.14)}`,
    '& .hover-effect': {
      opacity: 1,
      transform: 'scale(1.08)',
    },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.03)}, inset 0 -8px 20px ${alpha(theme.palette.primary.main, 0.02)}`,
    borderRadius: 'inherit',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.35s ease',
  },
  '&:hover::before': {
    transform: 'scaleX(1)',
  },
  '@media (max-width:600px)': {
    borderRadius: 12,
    padding: theme.spacing(1),
  },
}));

const FloatingIcon = styled(Box)(({ theme }) => ({
  animation: `${floatAnimation} 3s ease-in-out infinite`,
}));

// const ShimmerBox = styled(Box)(({ theme }) => ({
//   background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.4)}, transparent)`,
//   animation: `${shimmerAnimation} 2s infinite linear`,
//   backgroundSize: '1000px 100%',
// }));

const AnimatedListItem = styled(ListItem)(({ theme, active }) => ({
  animation: `${slideInLeft} 0.5s ease-out`,
  animationDelay: 'calc(var(--index) * 0.05s)',
  animationFillMode: 'both',
}));

const RotatingIcon = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  animation: `${rotate} 0.5s ease-out`,
}));

const Dashboard = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activePath, setActivePath] = useState('/dashboard');
  const [hoveredItem, setHoveredItem] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // Always start with 'today' as the default timeframe
  const [timeframe, setTimeframe] = useState('today');
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [loadingOverlayWhite, setLoadingOverlayWhite] = useState(false);
  const [weatherAnchorEl, setWeatherAnchorEl] = useState(null);

  const handleTimeframeChange = (key) => {
    if (key === timeframe) return;
    setLoadingOverlayWhite(true);
    setLoadingOverlay(true);
    setTimeout(() => {
      setTimeframe(key);
      setLoadingOverlay(false);
      setLoadingOverlayWhite(false);
    }, 2000);
  };

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location]);

  const isAtOverview = activePath === '/dashboard' || activePath === '/dashboard/';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    // show overlay loader then navigate (use white background per request)
    setLoadingOverlayWhite(true);
    setLoadingOverlay(true);
    setTimeout(() => {
      navigate(path);
      if (isMobile) {
        setMobileOpen(false);
      }
      setLoadingOverlay(false);
    }, 2000);
  };

  const menuItems = [
    { text: 'Overview', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Carbon Footprint', icon: <CloudIcon />, path: '/dashboard/carbon' },
    { text: 'Energy Management', icon: <ElectricBoltIcon />, path: '/dashboard/energy' },
    { text: 'Waste Management', icon: <RecyclingIcon />, path: '/dashboard/waste' },
    { text: 'Menu Analysis', icon: <RestaurantIcon />, path: '/dashboard/menu' },
    { text: 'Staff Management', icon: <PeopleIcon />, path: '/dashboard/staff' },
    { text: 'Inventory', icon: <InventoryIcon />, path: '/dashboard/inventory' },
    { text: 'Supply Chain', icon: <LocalShippingIcon />, path: '/dashboard/supply' },
    { text: 'Water Usage', icon: <WaterDropIcon />, path: '/dashboard/water' },
    { text: 'Financials', icon: <AttachMoneyIcon />, path: '/dashboard/financials' },
    { text: 'Reports', icon: <TimelineIcon />, path: '/dashboard/reports' },
    { text: 'POS API Integration', icon: <IntegrationInstructionsIcon />, path: '/dashboard/pos' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/dashboard/settings' },
  ];

  const stats = [
    { label: 'Carbon Footprint', valueNum: 12.5, unit: 't', change: -2.5, icon: <CloudIcon />, color: '#10b981' },
    { label: 'Energy Usage', valueNum: 2450, unit: 'kWh', change: -8.2, icon: <ElectricBoltIcon />, color: '#3b82f6' },
    { label: 'Water Usage', valueNum: 45, unit: 'm³', change: -5.1, icon: <WaterDropIcon />, color: '#0ea5e9' },
    { label: 'Waste Reduction', valueNum: 28, unit: '%', change: 12.3, icon: <RecyclingIcon />, color: '#8b5cf6' },
    { label: 'Cost Savings', valueNum: 1240, unit: '$', change: 15.7, icon: <AttachMoneyIcon />, color: '#f59e0b' },
    { label: 'Efficiency Score', valueNum: 84, unit: '/100', change: 4.2, icon: <CalculateIcon />, color: '#ec4899' },
  ];

  // Compute stats for each timeframe (simple demo multipliers)
  const statsByRange = {
    today: stats.map(s => ({ ...s, value: s.valueNum, valueLabel: s.unit === '%' ? `${s.valueNum}%` : (s.unit === '$' ? `$${s.valueNum.toLocaleString()}` : `${s.valueNum.toLocaleString()} ${s.unit}`) })),
    week: stats.map(s => {
      const factor = s.unit === '%' ? 1 : 7;
      const val = Math.round((s.valueNum * factor) * 100) / 100;
      return { ...s, value: val, valueLabel: s.unit === '%' ? `${val}%` : (s.unit === '$' ? `$${val.toLocaleString()}` : `${val.toLocaleString()} ${s.unit}`) };
    }),
    month: stats.map(s => {
      const factor = s.unit === '%' ? 1 : 30;
      const val = Math.round((s.valueNum * factor) * 100) / 100;
      return { ...s, value: val, valueLabel: s.unit === '%' ? `${val}%` : (s.unit === '$' ? `$${val.toLocaleString()}` : `${val.toLocaleString()} ${s.unit}`) };
    }),
  };

  const displayedStats = statsByRange[timeframe] || statsByRange.today;

  // Goals adjusted per timeframe (demo values)
  const baseGoals = [
    { label: 'Carbon Neutral by 2025', progress: 65, color: '#10b981' },
    { label: 'Zero Food Waste', progress: 42, color: '#8b5cf6' },
    { label: '100% Renewable Energy', progress: 78, color: '#3b82f6' },
    { label: 'Water Conservation', progress: 55, color: '#0ea5e9' },
  ];

  const goalsByRange = {
    today: baseGoals,
    week: baseGoals.map(g => ({ ...g, progress: Math.min(100, Math.round(g.progress * 1.05)) })),
    month: baseGoals.map(g => ({ ...g, progress: Math.min(100, Math.round(g.progress * 1.15)) })),
  };

  const displayedGoals = goalsByRange[timeframe] || goalsByRange.today;

  useEffect(() => {
    try { localStorage.setItem('rtn_zero_timeframe', timeframe); } catch (e) {}
  }, [timeframe]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Fade in timeout={500}>
          <Typography
            variant="h5"
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '1px',
            }}
          >
            🌱 RTN ZERO
          </Typography>
        </Fade>
        <Fade in timeout={700}>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Sustainable Restaurant Dashboard
          </Typography>
        </Fade>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {menuItems.map((item, index) => (
          <AnimatedListItem
            key={item.text}
            button
            onClick={() => handleNavigation(item.path)}
            onMouseEnter={() => setHoveredItem(index)}
            onMouseLeave={() => setHoveredItem(null)}
            sx={{
              mb: 1,
              borderRadius: '12px',
              transition: 'all 0.3s',
              position: 'relative',
              overflow: 'hidden',
              animationDelay: `${index * 50}ms`,
              backgroundColor: activePath === item.path 
                ? alpha(theme.palette.primary.main, 0.1) 
                : 'transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                transform: 'translateX(8px)',
                '& .menu-icon': {
                  transform: 'scale(1.1)',
                },
              },
              '&::before': activePath === item.path ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '60%',
                width: '4px',
                backgroundColor: theme.palette.primary.main,
                borderRadius: '0 4px 4px 0',
              } : {},
            }}
            style={{ '--index': index }}
          >
            <ListItemIcon 
              sx={{ 
                color: activePath === item.path ? 'primary.main' : 'text.secondary',
                transition: 'transform 0.3s',
                minWidth: 40,
              }}
              className="menu-icon"
            >
              <Grow in timeout={300 + index * 50}>
                {item.icon}
              </Grow>
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mr: 1,
              }}
              primaryTypographyProps={{
                fontWeight: activePath === item.path ? 600 : 400,
                color: activePath === item.path ? 'primary.main' : 'text.primary',
                noWrap: true,
              }}
            />
            {activePath === item.path && (
              <Zoom in timeout={300}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    ml: 1,
                  }}
                />
              </Zoom>
            )}
            {hoveredItem === index && activePath !== item.path && (
              <Fade in timeout={200}>
                <ChevronRightIcon 
                  sx={{ 
                    fontSize: 16, 
                    color: 'primary.main',
                    ml: 1,
                  }} 
                />
              </Fade>
            )}
          </AnimatedListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Zoom in timeout={500}>
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.2),
                transform: 'scale(1.02)',
              },
              transition: 'all 0.3s',
            }}
          >
            <ListItemIcon sx={{ color: 'error.main' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }}
            />
          </ListItem>
        </Zoom>
      </Box>
    </Box>
  );

  // --- Live Weather: geolocation + Open-Meteo fetching ---
  const [, setLocationAllowed] = useState(false);
  const [coords, setCoords] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [, setWeatherError] = useState(null);

  const requestLocation = () => {
    if (!navigator || !navigator.geolocation) {
      setWeatherError('Geolocation not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ latitude, longitude });
        setLocationAllowed(true);
        try { localStorage.setItem('rtn_zero_coords', JSON.stringify({ latitude, longitude })); } catch (e) {}
      },
      (err) => {
        setWeatherError('Location permission denied or unavailable');
        setLocationAllowed(false);
        try { localStorage.setItem('rtn_zero_location_prompt_shown', 'true'); } catch (e) {}
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  useEffect(() => {
    // If coordinates saved in localStorage, reuse them to avoid re-prompting.
    try {
      const saved = localStorage.getItem('rtn_zero_coords');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.latitude && parsed.longitude) {
          setCoords(parsed);
          setLocationAllowed(true);
          return;
        }
      }
    } catch (e) {}

    // Request location when the dashboard mounts (browser will show permission popup).
    requestLocation();
  }, []);

  useEffect(() => {
    let timer = null;
    if (!coords) return undefined;

    const fetchWeather = async () => {
      setWeatherLoading(true);
      setWeatherError(null);
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.latitude}&longitude=${coords.longitude}&current_weather=true&hourly=relativehumidity_2m,precipitation&timezone=auto`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Weather fetch failed');
        const data = await res.json();
        const curr = data.current_weather || null;
        let humidity = null;
        let precipitation = null;
        if (data.hourly && data.hourly.time && curr) {
          const idx = data.hourly.time.indexOf(curr.time);
          if (data.hourly.relativehumidity_2m) {
            humidity = idx >= 0 ? data.hourly.relativehumidity_2m[idx] : data.hourly.relativehumidity_2m[0];
          }
          if (data.hourly.precipitation) {
            precipitation = idx >= 0 ? data.hourly.precipitation[idx] : data.hourly.precipitation[0];
          }
        }
        setWeather({
          temp: curr?.temperature ?? null,
          windspeed: curr?.windspeed ?? null,
          winddir: curr?.winddirection ?? null,
          weathercode: curr?.weathercode ?? null,
          time: curr?.time ?? null,
          humidity,
          precipitation,
        });
      } catch (err) {
        setWeatherError('Unable to fetch weather data');
      } finally {
        setWeatherLoading(false);
      }
    };

    // initial fetch + periodic refresh every 5 minutes
    fetchWeather();
    timer = setInterval(fetchWeather, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [coords]);

  const weatherCodeMap = (code) => {
    const map = {
      0: { text: 'Clear', icon: <WbSunnyIcon sx={{ color: '#f59e0b' }} /> },
      1: { text: 'Mainly clear', icon: <WbSunnyIcon sx={{ color: '#fbbf24' }} /> },
      2: { text: 'Partly cloudy', icon: <CloudIcon sx={{ color: '#60a5fa' }} /> },
      3: { text: 'Overcast', icon: <CloudIcon sx={{ color: '#93c5fd' }} /> },
      45: { text: 'Fog', icon: <CloudIcon /> },
      48: { text: 'Depositing rime fog', icon: <CloudIcon /> },
      51: { text: 'Light drizzle', icon: <WaterDropIcon /> },
      61: { text: 'Rain', icon: <WaterDropIcon sx={{ color: '#0ea5e9' }} /> },
      71: { text: 'Snow', icon: <OpacityIcon sx={{ color: '#60a5fa' }} /> },
      80: { text: 'Rain showers', icon: <WaterDropIcon sx={{ color: '#38bdf8' }} /> },
    };
    return map[code] || { text: 'Unknown', icon: <CloudIcon /> };
  };

  

  const StatCard = ({ label, value, displayValue, change, icon, color, index, unit }) => {
    const [animValue, setAnimValue] = useState(0);
    useEffect(() => {
      let rafId = null;
      const duration = 800 + index * 80;
      const start = performance.now();
      const from = 0;
      const to = typeof value === 'number' ? value : 0;
      const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

      const step = (ts) => {
        const t = Math.min(1, (ts - start) / duration);
        const v = from + (to - from) * easeOutCubic(t);
        setAnimValue(v);
        if (t < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
      return () => { if (rafId) cancelAnimationFrame(rafId); };
    }, [value, index]);

    const format = (v) => {
      // preserve decimals when original value had them
      const hasFraction = typeof value === 'number' && Math.abs(value - Math.round(value)) > 0.01;
      if (unit === '%') return `${Math.round(v)}%`;
      if (unit === '$') return `$${Math.round(v).toLocaleString()}`;
      if (unit === '/100') return `${Math.round(v)}${unit}`;
      const numStr = hasFraction ? Number(v).toFixed(1) : String(Math.round(v));
      return `${Number(numStr).toLocaleString()} ${unit || ''}`.trim();
    };

    return (
      <Grow in timeout={500 + index * 100} style={{ transformOrigin: '0 0 0' }}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <AnimatedCard style={{ animationDelay: `${index * 100}ms` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                    {displayValue ? displayValue : format(animValue)}
                  </Typography>
                </Box>
                <FloatingIcon>
                  <Box
                    sx={{
                      backgroundColor: `${color}15`,
                      borderRadius: '16px',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Zoom in timeout={300}>
                      {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
                    </Zoom>
                    <Box
                      className="hover-effect"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `radial-gradient(circle at center, ${color}40, transparent 70%)`,
                        opacity: 0,
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </Box>
                </FloatingIcon>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <RotatingIcon>
                  {change >= 0 ? (
                    <TrendingUpIcon sx={{ color: '#10b981' }} />
                  ) : (
                    <TrendingDownIcon sx={{ color: '#ef4444' }} />
                  )}
                </RotatingIcon>
                <Typography
                  variant="body2"
                  sx={{ 
                    color: change >= 0 ? '#10b981' : '#ef4444', 
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {change >= 0 ? '+' : ''}{change}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  vs last month
                </Typography>
              </Box>
            </CardContent>
          </AnimatedCard>
        </Grid>
      </Grow>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* AppBar */}
      <Slide in direction="down" timeout={500}>
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.92)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
            backdropFilter: 'saturate(120%) blur(12px)',
            color: 'text.primary',
            boxShadow: '0 6px 30px rgba(2,6,23,0.08)',
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
            zIndex: theme.zIndex.drawer + 1,
            transition: 'all 0.35s',
          }}
        >
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: { sm: 'none' },
                color: 'primary.main',
              }}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                flexGrow: 1, 
                color: 'primary.main', 
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              <Fade in timeout={800}>
                <Box component="span" sx={{ animation: `${pulseAnimation} 2s infinite` }}>
                  Welcome back, {user?.name || 'Muhammad Kashan'}! 👋
                </Box>
              </Fade>
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              {['today','week','month'].map((key) => (
                <Chip
                  key={key}
                  label={key === 'today' ? 'Today' : key === 'week' ? 'This Week' : 'This Month'}
                  clickable
                  size="medium"
                  onClick={() => handleTimeframeChange(key)}
                  color={timeframe === key ? 'primary' : 'default'}
                  variant={timeframe === key ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 800, textTransform: 'none', px: 1.5, height: 36, fontSize: { xs: '0.78rem', sm: '0.9rem' }, mr: 0.5 }}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              <Zoom in timeout={400}>
                <IconButton sx={{ 
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    transform: 'scale(1.04)',
                  },
                  transition: 'all 0.25s',
                }}>
                  <Badge badgeContent={3} color="error" variant="dot">
                    <NotificationsIcon sx={{ color: 'primary.main' }} />
                  </Badge>
                </IconButton>
              </Zoom>

              <Zoom in timeout={500}>
                <Chip 
                  label="Sustainable" 
                  color="success" 
                  size="small" 
                  sx={{ 
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                    animation: `${pulseAnimation} 2s infinite`,
                  }}
                  icon={<CheckCircleIcon />}
                />
              </Zoom>

              {/* Weather compact display in AppBar */}
              <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                <Button
                  onClick={(e) => setWeatherAnchorEl(e.currentTarget)}
                  sx={{
                    textTransform: 'none',
                    minWidth: 80,
                    height: 36,
                    borderRadius: 3,
                    background: alpha(theme.palette.primary.main, 0.06),
                    color: 'text.primary',
                    '&:hover': { background: alpha(theme.palette.primary.main, 0.12) }
                  }}
                  startIcon={weather ? weatherCodeMap(weather.weathercode).icon : <WbSunnyIcon sx={{ color: 'primary.main' }} />}
                >
                  {weather && !weatherLoading ? `${weather.temp}°C` : '—'}
                </Button>
                <Popover
                  open={Boolean(weatherAnchorEl)}
                  anchorEl={weatherAnchorEl}
                  onClose={() => setWeatherAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { p: 2, borderRadius: 2, minWidth: 300 } }}
                >
                  <Grow in>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {weather ? `${Math.round(weather.temp)}°C — ${weatherCodeMap(weather.weathercode).text}` : 'Weather'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {weather?.time ? (() => {
                          try {
                            const d = new Date(weather.time);
                            return `${d.toLocaleDateString(undefined, { weekday: 'long' })}, ${d.toLocaleDateString()} • ${d.toLocaleTimeString()}`;
                          } catch (e) { return weather.time; }
                        })() : ''}
                      </Typography>

                      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Humidity</Typography>
                          <Typography variant="body2">{typeof weather?.humidity === 'number' ? `${Math.round(weather.humidity)}%` : 'No data'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Wind</Typography>
                          <Typography variant="body2">{typeof weather?.windspeed === 'number' ? `${Number(weather.windspeed).toFixed(1)} km/h` : 'No data'}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grow>
                </Popover>
              </Box>

              <Zoom in timeout={600}>
                <IconButton 
                  onClick={handleMenuClick} 
                  sx={{ 
                    p: 0.5,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main',
                      width: 36,
                      height: 36,
                      transition: 'all 0.3s',
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                </IconButton>
              </Zoom>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 1 }}
              >
                <Collapse in={Boolean(anchorEl)}>
                  <MenuItem onClick={() => handleNavigation('/dashboard/settings')}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    Settings
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Collapse>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
      </Slide>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidthMobile,
              borderRight: 'none',
              background: alpha(theme.palette.background.paper, 0.96),
              boxShadow: '8px 0 60px rgba(2,6,23,0.08)',
              borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.04)}`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              // borderRight: 'none',
              background: alpha(theme.palette.background.paper, 0.98),
              boxShadow: '8px 0 60px rgba(2,6,23,0.06)',
              borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.04)}`,
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          position: 'relative',
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
          minHeight: '100vh',
        }}
      >
        {loadingOverlay && (
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.modal + 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: loadingOverlayWhite ? theme.palette.common.white : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.06)})`,
          }}>
            <ClockLoader size={96} />
          </Box>
        )}
        <Collapse in timeout={300}>
          <Box>
            <Routes>
              <Route index element={<Overview />} />
              <Route path="carbon" element={<CarbonFootprint />} />
              <Route path="energy" element={<EnergyManagement />} />
              <Route path="waste" element={<WasteManagement />} />
              <Route path="pos" element={<POSIntegration />} />
              <Route path="pos/callback" element={<POSCallback />} />
              <Route path="staff" element={<StaffManagement />} />
            </Routes>

            {/* Dashboard Overview Content: render only on the index /dashboard route */}
            {isAtOverview && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                {/* Live Weather Tile */}
                <Grid container spacing={3}>
                  {/* Weather moved to AppBar popover */}
                </Grid>

                {/* Stats Grid */}
                <Grid container spacing={3}>
                  {displayedStats.map((stat, index) => (
                    <StatCard key={index} index={index} {...stat} />
                  ))}
                </Grid>

                {/* Progress Section */}
                <Slide in direction="up" timeout={500}>
                  <div>
                    <AnimatedCard>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <EmojiEventsIcon color="primary" />
                          <Typography variant="h6" fontWeight="bold">
                            Sustainability Goals Progress
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                          {displayedGoals.map((goal, index) => (
                            <Box key={index}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" fontWeight="500">{goal.label}</Typography>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {goal.progress}%
                                </Typography>
                              </Box>
                              <Box sx={{ position: 'relative', height: 10 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={goal.progress} 
                                  sx={{ 
                                    height: 10, 
                                    borderRadius: 5,
                                    backgroundColor: alpha(goal.color, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 5,
                                      background: `linear-gradient(90deg, ${goal.color}, ${alpha(goal.color, 0.7)})`,
                                      animation: `${pulseAnimation} 2s infinite ease-in-out`,
                                      transition: 'width 1.5s ease-in-out',
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Zoom in timeout={800}>
                          <Button 
                            size="small" 
                            color="primary" 
                            endIcon={<TrendingUpIcon />}
                            sx={{ fontWeight: 'bold' }}
                          >
                            View All Goals
                          </Button>
                        </Zoom>
                      </CardActions>
                    </AnimatedCard>
                  </div>
                </Slide>

                {/* Quick Actions & Achievements */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Zoom in timeout={600} style={{ transitionDelay: '200ms' }}>
                      <div>
                        <AnimatedCard sx={{ height: '100%' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                              <BoltIcon color="primary" />
                              <Typography variant="h6" fontWeight="bold">
                                Quick Actions
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              {[
                                { label: 'Add Menu Item', icon: <RestaurantIcon /> },
                                { label: 'Update Inventory', icon: <InventoryIcon /> },
                                { label: 'View Reports', icon: <TimelineIcon /> },
                                { label: 'Track Waste', icon: <RecyclingIcon /> },
                              ].map((action, index) => (
                                <Grid item xs={6} key={index}>
                                  <Zoom in timeout={400 + index * 100}>
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      startIcon={action.icon}
                                      sx={{ 
                                        height: 56,
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                        color: 'white',
                                        fontWeight: 'bold',
                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                          transform: 'scale(1.05)',
                                          boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
                                        }
                                      }}
                                    >
                                      {action.label}
                                    </Button>
                                  </Zoom>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </AnimatedCard>
                      </div>
                    </Zoom>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Zoom in timeout={700} style={{ transitionDelay: '300ms' }}>
                      <div>
                        <AnimatedCard sx={{ height: '100%' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                              <EmojiEventsIcon color="success" />
                              <Typography variant="h6" fontWeight="bold">
                                Recent Achievements
                              </Typography>
                            </Box>
                            <List disablePadding>
                              {[
                                'Reduced energy consumption by 15%',
                                'Saved 200kg of food waste this month',
                                'Recycled 85% of total waste',
                                'Installed smart water meters',
                              ].map((achievement, index) => (
                                <Fade in timeout={300 + index * 100} key={index}>
                                  <ListItem 
                                    sx={{ 
                                      py: 1.5,
                                      borderBottom: index < 3 ? '1px solid' : 'none',
                                      borderColor: 'divider',
                                      animation: `${fadeInUp} 0.5s ease-out`,
                                      animationDelay: `${index * 100}ms`,
                                      animationFillMode: 'both',
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={achievement}
                                      primaryTypographyProps={{ fontWeight: 500 }}
                                    />
                                  </ListItem>
                                </Fade>
                              ))}
                            </List>
                          </CardContent>
                        </AnimatedCard>
                      </div>
                    </Zoom>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default Dashboard;